import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";

const API_BASE = String(process.env.REACT_APP_API_BASE || "https://api.chainzsolutions.com")
  .replace(/['"]/g, "")
  .trim()
  .replace(/\/$/, "");

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function intentFromSearch(params) {
  const raw = (params.get("intent") || "").toLowerCase();
  return raw === "partnership" || raw === "partner" ? "Partnership" : "Demo";
}

function formatWhen(iso) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Cairo",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

function monthMatrix(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const startDow = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  while (cells.length % 7) cells.push(null);
  return cells;
}

const emptyForm = {
  name: "",
  company: "",
  phone: "",
  email: "",
  estimatedVolume: "",
  notes: "",
};

export function BookDemoPage() {
  const location = useLocation();
  const [params] = useSearchParams();
  const manage = location.pathname.includes("/manage");
  const token = params.get("token") || "";
  const intent = intentFromSearch(params);

  const [step, setStep] = useState(manage ? 2 : 1);
  const [form, setForm] = useState(emptyForm);
  const [dates, setDates] = useState({});
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  });
  const [pickedDay, setPickedDay] = useState("");
  const [occurrenceId, setOccurrenceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [existing, setExisting] = useState(null);

  const loadAvailability = useCallback(() => {
    return axios.get(`${API_BASE}/demos/availability`).then((res) => {
      setDates(res.data?.data?.dates || {});
    });
  }, []);

  useEffect(() => {
    loadAvailability().catch(() => setError("Could not load available times."));
  }, [loadAvailability]);

  useEffect(() => {
    if (!manage || !token) return;
    axios
      .get(`${API_BASE}/demos/booking`, { params: { token } })
      .then((res) => setExisting(res.data.data))
      .catch((err) => setError(err.response?.data?.message || "This booking link is invalid."));
  }, [manage, token]);

  const availableDays = useMemo(() => new Set(Object.keys(dates)), [dates]);
  const cells = useMemo(() => monthMatrix(cursor.y, cursor.m), [cursor]);
  const daySlots = dates[pickedDay] || [];
  const selectedSlot = daySlots.find((s) => s.id === occurrenceId);

  const title = manage
    ? "Reschedule your demo"
    : intent === "Partnership"
      ? "Become a Partner"
      : "Book a Demo";

  const subtitle = manage
    ? existing
      ? `Currently booked for ${existing.when}. Pick a new time or cancel.`
      : "Loading your booking…"
    : intent === "Partnership"
      ? "Tell us about your brand, then pick a time. We will call you."
      : "A 1:1 walkthrough of Chainz. We will call the number you enter.";

  const onForm = (e) => {
    e.preventDefault();
    setError("");
    setStep(2);
  };

  const submitBook = async () => {
    if (!occurrenceId) {
      setError("Pick a time slot.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (manage) {
        const res = await axios.post(`${API_BASE}/demos/reschedule`, { token, occurrenceId });
        setDone({ when: res.data.data.when, rescheduled: true });
      } else {
        const res = await axios.post(`${API_BASE}/demos/book`, {
          ...form,
          occurrenceId,
          intent,
        });
        setDone(res.data.data);
      }
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not save the booking.");
      loadAvailability();
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async () => {
    if (!window.confirm("Cancel this demo? The seat will open for someone else.")) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/demos/cancel`, { token });
      setDone({ cancelled: true });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || "Could not cancel.");
    } finally {
      setLoading(false);
    }
  };

  const shiftMonth = (delta) => {
    setPickedDay("");
    setOccurrenceId("");
    setCursor((c) => {
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  if (step === 3 && done) {
    return (
      <div id="book-page" style={{ padding: "80px clamp(16px, 4vw, 40px) 90px" }}>
        <div className="rv in card" style={{ maxWidth: 640, margin: "0 auto", padding: 48, textAlign: "center" }}>
          <div style={{ fontSize: "clamp(28px, 5vw, 40px)", fontWeight: 800, letterSpacing: "-0.01em" }}>
            {done.cancelled ? "Demo cancelled" : done.rescheduled ? "Demo rescheduled" : "You're booked"}
          </div>
          <div style={{ fontSize: 16, color: "#6b6470", marginTop: 14, lineHeight: 1.65 }}>
            {done.cancelled
              ? "Your seat was released. You can book again whenever you are ready."
              : `We will call you at ${done.when || formatWhen(done.start)}. Check your email for reschedule and cancel links.`}
          </div>
          <Link className="btn red" to={done.cancelled ? `/book?intent=${intent === "Partnership" ? "partnership" : "demo"}` : "/"} style={{ marginTop: 28, padding: "14px 26px", display: "inline-flex" }}>
            {done.cancelled ? "Book again" : "Back to home"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div id="book-page" style={{ padding: "40px clamp(16px, 4vw, 40px) 90px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div className="rv in" style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ fontSize: "clamp(30px, 6.5vw, 44px)", fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.1 }}>
            {title}
          </div>
          <div style={{ fontSize: 16, color: "#6b6470", marginTop: 14, lineHeight: 1.65 }}>{subtitle}</div>
          {!manage && (
            <div style={{ marginTop: 16, fontSize: 13, color: "#6b6470" }}>
              {intent === "Partnership" ? "Partnership track" : "Demo only"} · Africa/Cairo · 24 hours notice
            </div>
          )}
        </div>

        {error ? (
          <div className="card" style={{ padding: 16, marginBottom: 20, color: "#7D010C", fontSize: 14 }}>
            {error}
          </div>
        ) : null}

        {step === 1 && (
          <form className="card" style={{ padding: 36 }} onSubmit={onForm}>
            <div className="cform-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label className="clabel" htmlFor="name">Full name</label>
                <input className="cfield" id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="clabel" htmlFor="company">Company / brand</label>
                <input className="cfield" id="company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div>
                <label className="clabel" htmlFor="phone">Phone</label>
                <input className="cfield" id="phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="clabel" htmlFor="email">Work email</label>
                <input className="cfield" id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <label className="clabel" htmlFor="volume">Monthly order volume</label>
              <input className="cfield" id="volume" placeholder="e.g. 500–1,000" value={form.estimatedVolume} onChange={(e) => setForm({ ...form, estimatedVolume: e.target.value })} />
            </div>
            <div style={{ marginTop: 16 }}>
              <label className="clabel" htmlFor="notes">Notes</label>
              <textarea className="cfield" id="notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <button className="btn red" type="submit" style={{ marginTop: 24, padding: "14px 26px" }}>
              Choose a time
            </button>
          </form>
        )}

        {step === 2 && (
          <div className="card" style={{ padding: 36 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              {!manage && (
                <button type="button" onClick={() => setStep(1)} style={{ background: "none", border: "none", fontWeight: 600, cursor: "pointer" }}>
                  ← Back
                </button>
              )}
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginLeft: "auto" }}>
                <button
                  type="button"
                  className="book-month-nav"
                  onClick={() => shiftMonth(-1)}
                  aria-label="Previous month"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <strong style={{ minWidth: 148, textAlign: "center", color: "#1a1416" }}>
                  {new Date(cursor.y, cursor.m, 1).toLocaleString("en-GB", { month: "long", year: "numeric" })}
                </strong>
                <button
                  type="button"
                  className="book-month-nav"
                  onClick={() => shiftMonth(1)}
                  aria-label="Next month"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="book-cal">
              {DOW.map((d) => (
                <div key={d} className="dow">{d}</div>
              ))}
              {cells.map((day, i) => {
                const has = day && availableDays.has(day);
                return (
                  <button
                    key={day || `e-${i}`}
                    type="button"
                    className={`book-day${pickedDay === day ? " on" : ""}`}
                    disabled={!has}
                    onClick={() => {
                      setPickedDay(day);
                      setOccurrenceId("");
                    }}
                  >
                    {day ? Number(day.slice(-2)) : ""}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: 22, display: "grid", gap: 10 }}>
              {!pickedDay && <div style={{ color: "#6b6470", fontSize: 14 }}>Select a highlighted day.</div>}
              {pickedDay && daySlots.length === 0 && <div style={{ color: "#6b6470", fontSize: 14 }}>No remaining seats that day.</div>}
              {daySlots.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`book-slot${occurrenceId === s.id ? " on" : ""}`}
                  onClick={() => setOccurrenceId(s.id)}
                >
                  <span>{formatWhen(s.start)}</span>
                  <span style={{ color: "#6b6470", fontWeight: 500 }}>{s.remainingSeats} seat{s.remainingSeats === 1 ? "" : "s"} · {s.durationMinutes} min</span>
                </button>
              ))}
            </div>

            <button
              className="btn red"
              type="button"
              disabled={loading || !occurrenceId}
              onClick={submitBook}
              style={{ marginTop: 24, padding: "14px 26px", opacity: occurrenceId ? 1 : 0.5 }}
            >
              {loading ? "Saving…" : manage ? "Reschedule" : selectedSlot ? `Confirm ${formatWhen(selectedSlot.start)}` : "Confirm slot"}
            </button>

            {manage && (
              <button type="button" onClick={cancelBooking} disabled={loading} style={{ display: "block", marginTop: 16, background: "none", border: "none", color: "#7D010C", fontWeight: 600, cursor: "pointer" }}>
                Cancel this demo
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
